import React, { useCallback, useEffect, useRef } from 'react';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { MetricBar } from 'apps/sigma2-ui/src/explore-section/components/MetricBar';
import { useHeatmapExport } from 'apps/sigma2-ui/src/explore-section/hooks/useHeatmapExport';
import { HeatmapFilterGrouping } from 'libs/end-to-end/src/heatmap-analysis';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import cn from 'classnames';
import { IconDownload2Svg } from 'apps/sigma2-ui/src/components/svg/IconDownload2';
import { downloadCSV } from 'apps/sigma2-ui/src/lib/utils/csv-export';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { CSVDownloadButton } from 'apps/sigma2-ui/src/components/CSVDownloadButton';

interface ExploreHeaderProps {
  selectedFilterDefinition: string;
  onSelectFilterDefinition: (filterDefinition: string) => void;
  filterDefinitions: Array<{ value: string; label: string }>;
  filteredGrouping: HeatmapFilterGrouping | null;
}

/**
 * Header component for the heatmap analysis section.
 * Displays a metric selection bar, a color gradient legend, an information tooltip,
 * and a CSV download button.
 *
 * @param {Object} props
 * @param {string} props.selectedFilterDefinition - Currently selected metric filter
 * @param {Function} props.onSelectFilterDefinition - Callback when a new metric is selected
 * @param {Array<{value: string, label: string}>} props.filterDefinitions - Available metric options
 * @param {HeatmapFilterGrouping | null} props.filteredGrouping - Grouped data for CSV export
 * @returns {JSX.Element} The rendered header component
 */
export const ExploreHeader: React.FC<ExploreHeaderProps> = ({
  selectedFilterDefinition,
  onSelectFilterDefinition,
  filterDefinitions,
  filteredGrouping,
}) => {
  const language = useLanguage();

  const [infoTooltipVisible, setInfoTooltipVisible] = React.useState(false);
  const [infoTooltipPosition, setInfoTooltipPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);

  const csvData = useHeatmapExport(filteredGrouping);

  const handleInfoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const button = e.currentTarget.getBoundingClientRect();

    setInfoTooltipPosition({
      top: button.bottom + window.scrollY + 5,
      left: button.left + window.scrollX - 300,
    });

    setInfoTooltipVisible(!infoTooltipVisible);
  };

  const downloadButtonRef = useRef<HTMLDivElement | null>(null);

  const handleDownloadClick = useCallback(() => {
    downloadCSV(csvData, 'heatmap-data.csv');
  }, [csvData]);

  useEffect(() => {
    const handle = () => {
      setInfoTooltipVisible(false);
    };
    window.addEventListener('resize', handle);

    return () => {
      window.removeEventListener('resize', handle);
    };
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <div
      className={cn(
        'flex flex-wrap justify-between items-center align-middle gap-4 group-[]/dashboard:ml-4',
        {
          'mb-2': isMobile,
          'mb-6': !isMobile,
          'w-full': isMobile,
        },
      )}
    >
      <MetricBar
        selectedValue={selectedFilterDefinition}
        onSelect={onSelectFilterDefinition}
        options={filterDefinitions}
        labelTranslatedContent={language.heatmapAnalysisMetricBarText()}
      />

      <div
        className={cn(
          'flex flex-col sm:flex-row w-auto items-center gap-4 sm:gap-2 flex-wrap',
          {
            'w-full': isMobile,
          },
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1">
            <S2Typography
              variant={isMobile ? 'body-7-medium' : 'body-6-medium'}
              textColorVariant={'inherit'}
              as="span"
              translatedContent={language.exploreHeatmapHighImpactArea()}
            />
          </div>
          <div className="flex-1 flex h-3 w-40 rounded-lg overflow-hidden">
            <div className="flex-1 bg-violet-800" />
            <div className="flex-1 bg-violet-700" />
            <div className="flex-1 bg-violet-600" />
            <div className="flex-1 bg-violet-500" />
            <div className="flex-1 bg-violet-400" />
            <div className="flex-1 bg-violet-300" />
            <div className="flex-1 bg-violet-200" />
            <div className="flex-1 bg-violet-100" />
            <div className="flex-1 bg-gray-100" />
          </div>
          <div className="ml-2 flex-1">
            <S2Typography
              variant={isMobile ? 'body-7-medium' : 'body-6-medium'}
              textColorVariant={'inherit'}
              as="span"
              translatedContent={language.exploreHeatmapLowImpactArea()}
            />
          </div>
          <button
            onClick={handleInfoClick}
            className="text-gray-400 w-5 h-5 mr-2 hover:text-gray-600 transition-colors"
          >
            <IconInformationSvg size={20} />
          </button>
          {!isMobile && (
            <CSVDownloadButton data={csvData} filename="heatmap-data.csv" />
          )}
        </div>
        {infoTooltipVisible && (
          <div
            className="fixed z-[50] bg-white rounded-lg border-1 border-gray-150 p-4 w-[300px]"
            style={{
              top: `${infoTooltipPosition?.top}px`,
              left: `${infoTooltipPosition?.left}px`,
            }}
          >
            <S2Typography
              variant="body-7"
              textColorVariant="gray-600"
              translatedContent={language.exploreHeatmapAnalysisToastInfo()}
            />
          </div>
        )}
        {isMobile && (
          <div
            ref={downloadButtonRef}
            className={'inline-block w-full align-middle'}
          >
            <TouchTarget
              disabled={false}
              content={({ hovered, focused, active }) => {
                return (
                  <Spacing
                    direction="horizontal"
                    size="sm"
                    className={cn(
                      `flex bg-white text-royal-700 w-full text-center justify-center items-center rounded h-[40px] px-6 border-box transition-colors whitespace-nowrap select-none border-[1.5px] cursor-pointer border-royal-900'`,
                      {
                        'bg-royal-100': hovered || focused,
                        'bg-royal-300': active,
                      },
                    )}
                  >
                    <IconDownload2Svg />
                    <S2Typography
                      translatedContent={language.exploreHeatmapDownloadButtonLabel()}
                      variant={'headline-7-alt'}
                      textColorVariant={hovered ? 'royal-700' : 'gray-900'}
                    />
                  </Spacing>
                );
              }}
              behavior={{ type: 'onClick', onClick: handleDownloadClick }}
            ></TouchTarget>
          </div>
        )}
      </div>
    </div>
  );
};
